import { useActorRef, useSelector } from '@xstate/react';
import { useEffect, useRef, type ReactNode } from 'react';
import type { SnapshotFrom } from 'xstate';
import { Button } from '~/components';
import { NegotiationTermsSummary } from '~/features/market/negotiations/sub-components/NegotiationTermsSummary';
import { MarketActionRow } from '~/features/shared/market-content';
import { ModalDialog } from '~/features/vault/documents/ModalDialog';
import { b2bInquiryApplyMachine, type B2BApplySource } from './b2bInquiryApply.machine';

type Snapshot = SnapshotFrom<typeof b2bInquiryApplyMachine>;
const selectOpen = (s: Snapshot) => s.context.open;
const selectSource = (s: Snapshot) => s.context.source;
const selectDraft = (s: Snapshot) => s.context.draft;
const selectError = (s: Snapshot) => s.context.error;
const selectResult = (s: Snapshot) => s.context.result;
const selectBusy = (s: Snapshot) => s.matches('sending') || s.matches('recovering');
const selectReady = (s: Snapshot) => s.matches('ready');
const selectUnavailable = (s: Snapshot) => s.matches('unavailable');
const selectUncertain = (s: Snapshot) => s.matches('uncertain');

export const B2BInquiryApplyFlow = ({
  children,
  onApplied,
}: {
  children: (controls: { open: (source: B2BApplySource) => void; blocked: boolean }) => ReactNode;
  onApplied: () => void;
}) => {
  const actor = useActorRef(b2bInquiryApplyMachine);
  const open = useSelector(actor, selectOpen);
  const source = useSelector(actor, selectSource);
  const draft = useSelector(actor, selectDraft);
  const error = useSelector(actor, selectError);
  const result = useSelector(actor, selectResult);
  const busy = useSelector(actor, selectBusy);
  const ready = useSelector(actor, selectReady);
  const unavailable = useSelector(actor, selectUnavailable);
  const uncertain = useSelector(actor, selectUncertain);
  const onAppliedRef = useRef(onApplied);
  useEffect(() => {
    onAppliedRef.current = onApplied;
  }, [onApplied]);
  useEffect(() => {
    if (result) onAppliedRef.current();
  }, [result]);

  return (
    <>
      {uncertain && !open && source && (
        <div className="mb-3 space-y-2">
          <p role="status">The application outcome for {source.title} is unconfirmed.</p>
          <Button
            type="button"
            label="Review original application"
            onClick={() => actor.send({ type: 'OPEN', source })}
          />
        </div>
      )}
      {children({
        open: (selected) => actor.send({ type: 'OPEN', source: selected }),
        blocked: open || busy || uncertain,
      })}
      <ModalDialog
        open={open}
        title="Apply with listing terms"
        xButton
        closeDisabled={busy}
        onClose={() => actor.send({ type: 'CLOSE' })}
      >
        <p className="body-text-sb">{source?.title}</p>
        <p className="my-3 text-sm">
          Apply starts an initial offer using these listing terms on this inquiry. It does not
          accept an offer or reveal identities.
        </p>
        {draft && <NegotiationTermsSummary terms={draft.terms} />}
        <p role="status" aria-live="polite" className="my-3 min-h-5 text-sm">
          {error ??
            (busy
              ? 'Checking application…'
              : !ready && !draft
                ? 'Loading current listing terms…'
                : '')}
        </p>
        {uncertain ? (
          <>
            <p className="mb-3 text-sm">
              Retry sends the original terms, not subsequent listing changes. Discarding local
              tracking does not cancel or undo an offer on the server.
            </p>
            <MarketActionRow
              actions={[
                {
                  label: 'Refresh application outcome',
                  onClick: () => actor.send({ type: 'REFRESH' }),
                },
                { label: 'Retry original terms', onClick: () => actor.send({ type: 'RETRY' }) },
                {
                  label: 'Discard local tracking',
                  onClick: () => actor.send({ type: 'DISCARD_RETRY' }),
                },
              ]}
            />
          </>
        ) : (
          <MarketActionRow
            actions={[
              ...(unavailable
                ? [
                    {
                      label: 'Reload listing terms',
                      onClick: () => actor.send({ type: 'RELOAD_DRAFT' }),
                    },
                  ]
                : []),
              {
                label: 'Confirm Apply',
                disabled: !ready,
                onClick: () => actor.send({ type: 'CONFIRM' }),
              },
              { label: 'Cancel', disabled: busy, onClick: () => actor.send({ type: 'CLOSE' }) },
            ]}
          />
        )}
      </ModalDialog>
    </>
  );
};
